import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GraduationCap, MessageCircle, ClipboardList, Award, Play, Sparkles, Heart } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [apps, setApps] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/courses").then((r) => setCourses(r.data.courses || []));
    api.get("/courses/progress/me").then((r) => setProgress(r.data.progress || []));
    api.get("/applications/me").then((r) => setApps(r.data.applications || []));
    api.get("/foundation/my-sponsors").then((r) => setSponsors(r.data.sponsors || [])).catch(() => {});
  }, [user]);

  if (loading) return <Section className="pt-40 pb-40"><Container><div className="text-[color:var(--wk-bone-dim)]">Loading…</div></Container></Section>;
  if (!user) return (
    <Section className="pt-40 pb-40">
      <Container>
        <Eyebrow>Access required</Eyebrow>
        <DisplayH1 className="mt-6 max-w-2xl">Please sign in to view your dashboard.</DisplayH1>
        <div className="mt-8"><Link className="btn-gold" to="/login">Sign in</Link></div>
      </Container>
    </Section>
  );

  const completedByCourse = progress.reduce((m, p) => {
    m[p.course_id] = (m[p.course_id] || 0) + (p.completed ? 1 : 0);
    return m;
  }, {});

  return (
    <>
      <Section testid="dashboard-hero" className="pt-32 pb-16 border-b border-white/10">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Eyebrow>Student Portal</Eyebrow>
              <DisplayH1 className="mt-4">Welcome back, {user.name?.split(" ")[0] || "Wolfkeeper"}.</DisplayH1>
            </div>
            {user.picture && (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full border border-white/10" />
            )}
          </div>
        </Container>
      </Section>

      <Section testid="dashboard-summary" className="py-16">
        <Container>
          <div className="grid gap-6 md:grid-cols-4">
            {[
              { Icon: GraduationCap, label: "Courses in progress", value: Object.keys(completedByCourse).length || courses.length },
              { Icon: Award, label: "Lessons completed", value: progress.filter((p) => p.completed).length },
              { Icon: ClipboardList, label: "Applications", value: apps.length },
              { Icon: MessageCircle, label: "AI conversations", value: "∞" },
            ].map(({ Icon, label, value }) => (
              <Reveal key={label}>
                <div className="card-lux p-6">
                  <Icon className="text-[color:var(--wk-gold)]" size={20} />
                  <div className="font-serif-display text-4xl mt-4">{value}</div>
                  <div className="overline text-[10px] mt-2">{label}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <Link to="/assistant" data-testid="dashboard-cta-assistant" className="card-lux p-6 hover:border-[color:var(--wk-gold)]/60 transition-colors">
              <Sparkles className="text-[color:var(--wk-gold)]" size={18} />
              <div className="mt-3 font-serif-display text-2xl">Talk to TONIA</div>
              <div className="text-sm text-[color:var(--wk-bone-dim)] mt-1">Warm, wise, 24/7 AI mentor</div>
            </Link>
            <Link to="/founder-qa" data-testid="dashboard-cta-founder" className="card-lux p-6 hover:border-[color:var(--wk-gold)]/60 transition-colors">
              <MessageCircle className="text-[color:var(--wk-gold)]" size={18} />
              <div className="mt-3 font-serif-display text-2xl">Ask the Founder</div>
              <div className="text-sm text-[color:var(--wk-bone-dim)] mt-1">Direct line to Toriano · Wolfkeeper Plus</div>
            </Link>
            <Link to="/apply" data-testid="dashboard-cta-apply" className="card-lux p-6 hover:border-[color:var(--wk-gold)]/60 transition-colors">
              <ClipboardList className="text-[color:var(--wk-gold)]" size={18} />
              <div className="mt-3 font-serif-display text-2xl">New Application</div>
              <div className="text-sm text-[color:var(--wk-bone-dim)] mt-1">Start a new program application</div>
            </Link>
          </div>
        </Container>
      </Section>

      <Section testid="dashboard-courses" className="pb-20">
        <Container>
          <div className="flex items-end justify-between mb-8">
            <div>
              <Eyebrow>Continue Learning</Eyebrow>
              <div className="mt-2 font-serif-display text-3xl">Your course library</div>
            </div>
            <Link to="/university" className="btn-ghost h-11">Browse all courses</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => {
              const total = c.lessons.length;
              const done = completedByCourse[c.course_id] || 0;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <Link to={`/university/${c.course_id}`} key={c.course_id} className="card-lux overflow-hidden group" data-testid={`dashboard-course-${c.course_id}`}>
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={c.image} alt="" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                    <div className="absolute inset-0 cinema-overlay" />
                    <div className="absolute top-4 left-4 overline text-[10px] px-3 py-1 rounded-full glass border-white/20">{c.level}</div>
                    <Play className="absolute bottom-4 right-4 text-white/80" />
                  </div>
                  <div className="p-5">
                    <div className="font-serif-display text-xl">{c.title}</div>
                    <div className="text-xs text-[color:var(--wk-bone-dim)] mt-1">{c.instructor} · {c.duration}</div>
                    <div className="mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[color:var(--wk-gold)]" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">{done} of {total} lessons · {pct}%</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section testid="dashboard-apps" className="pb-16">
        <Container>
          <Eyebrow>Applications</Eyebrow>
          <div className="mt-6 card-lux overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[color:var(--wk-bone-dim)] border-b border-white/10">
                <tr>
                  <th className="text-left p-4 font-normal overline text-[10px]">Reference</th>
                  <th className="text-left p-4 font-normal overline text-[10px]">Program</th>
                  <th className="text-left p-4 font-normal overline text-[10px]">Status</th>
                  <th className="text-left p-4 font-normal overline text-[10px]">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {apps.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-[color:var(--wk-bone-dim)]">No applications yet. <Link to="/apply" className="text-[color:var(--wk-gold)] underline ml-1">Start one →</Link></td></tr>
                )}
                {apps.map((a) => (
                  <tr key={a.application_id} className="border-b border-white/5">
                    <td className="p-4 font-mono text-[color:var(--wk-gold)]">{a.application_id}</td>
                    <td className="p-4 capitalize">{a.program_slug.replace(/-/g, " ")}</td>
                    <td className="p-4"><span className="px-3 py-1 rounded-full glass text-xs">{a.status}</span></td>
                    <td className="p-4 text-[color:var(--wk-bone-dim)]">{a.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>

      {/* Sponsor Impact Feed */}
      <Section testid="dashboard-sponsor-feed" className="pb-32">
        <Container>
          <div className="flex items-end justify-between mb-6">
            <div>
              <Eyebrow>Your Sponsored Pack</Eyebrow>
              <div className="mt-2 font-serif-display text-3xl">Fresh notes from the field.</div>
            </div>
            <Link to="/foundation/dogs" className="btn-ghost h-11">Sponsor another dog</Link>
          </div>
          {sponsors.length === 0 ? (
            <div className="card-lux p-8 text-center text-[color:var(--wk-bone-dim)]">
              <Heart className="text-[color:var(--wk-gold)] mx-auto" />
              <div className="mt-3 font-serif-display text-2xl text-[color:var(--wk-bone)]">You're not sponsoring a dog yet.</div>
              <p className="mt-1 text-sm">Meet the pack and follow one home.</p>
              <Link to="/foundation/dogs" className="btn-gold mt-6 inline-flex">Meet the pack</Link>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {sponsors.map((s) => (
                <Reveal key={s.dog_id}>
                  <Link to={`/foundation/dogs/${s.dog_id}`} className="card-lux overflow-hidden group block" data-testid={`sponsor-feed-${s.dog_id}`}>
                    <div className="grid grid-cols-3 gap-0">
                      <div className="relative aspect-square overflow-hidden col-span-1">
                        <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 cinema-overlay" />
                      </div>
                      <div className="col-span-2 p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-serif-display text-2xl leading-none">{s.name}</div>
                            <div className="text-xs text-[color:var(--wk-bone-dim)] mt-1">{s.breed} · {s.stage}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-[color:var(--wk-gold)] font-medium">${s.your_total.toLocaleString()}</div>
                            <div className="text-[10px] overline text-[color:var(--wk-bone-dim)]">{s.cadence || "sponsor"}</div>
                          </div>
                        </div>

                        {s.updates.length > 0 ? (
                          <div className="mt-4 space-y-3">
                            {s.updates.slice(0, 2).map((u) => (
                              <div key={u.update_id} className="pt-3 border-t border-white/10">
                                <div className="overline text-[10px] text-[color:var(--wk-gold)]">
                                  {new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · {u.author}
                                </div>
                                <div className="mt-1 font-serif-display text-lg leading-tight">{u.title}</div>
                                <p className="mt-1 text-sm text-[color:var(--wk-bone-dim)] leading-relaxed line-clamp-2">{u.body}</p>
                                {u.photo_url && (
                                  <div className="mt-2 relative rounded-lg overflow-hidden aspect-video">
                                    <img src={u.photo_url} alt="" className="w-full h-full object-cover" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-4 text-xs text-[color:var(--wk-bone-dim)] italic">First update coming this month.</div>
                        )}
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
